import { useState } from 'react';
import { Swords, Shield, Grip } from 'lucide-react';
import { normalizeClassName } from '@/utils/classNameUtils';

interface ClassIconProps {
  characterClass: string;
  classType: 'attacker' | 'supporter';
  supPossible?: number; // 1이면 서포터도 가능 (딜러인데 서폿 가능)
  atkPossible?: number; // 1이면 딜러도 가능 (서포터인데 딜 가능)
  size?: number;
}

export default function ClassIcon({
  characterClass,
  classType,
  supPossible = 0,
  atkPossible = 0,
  size = 40
}: ClassIconProps) {
  const [imageError, setImageError] = useState(false);

  // 둘 다 가능한지 확인
  const isFlex = (classType === 'attacker' && supPossible === 1) ||
                 (classType === 'supporter' && atkPossible === 1);

  // 색상 결정
  const getColorClass = () => {
    if (isFlex) return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'; // 둘다 가능
    if (classType === 'attacker') return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'; // 딜러
    return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30'; // 서포터
  };

  // 필터 색상 클래스
  const getFilterClass = () => {
    if (isFlex) return 'filter-green';
    if (classType === 'attacker') return 'filter-red';
    return 'filter-blue';
  };

  // Fallback 아이콘 결정
  const FallbackIcon = () => {
    const iconSize = size * 0.5; // 아이콘은 원의 50% 크기
    if (isFlex) return <Grip className="w-full h-full" style={{ width: iconSize, height: iconSize }} />;
    if (classType === 'attacker') return <Swords className="w-full h-full" style={{ width: iconSize, height: iconSize }} />;
    return <Shield className="w-full h-full" style={{ width: iconSize, height: iconSize }} />;
  };

  // 클래스 이름 정규화 (신의기사 → 발키리, 심판자 → 홀리나이트)
  const normalizedClassName = normalizeClassName(characterClass);

  // 아이콘 경로
  const iconPath = `/class-icons/${normalizedClassName}.svg`;

  // 이미지 로드 실패 시 fallback 표시
  if (imageError) {
    return (
      <div
        className={`rounded-full flex items-center justify-center ${getColorClass()}`}
        style={{ width: size, height: size }}
      >
        <FallbackIcon />
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <div className={`w-full h-full rounded-full flex items-center justify-center ${getColorClass()}`}>
        <img
          src={iconPath}
          alt={characterClass}
          className={`object-contain ${getFilterClass()}`}
          style={{ width: size * 0.8, height: size * 0.8 }}
          onError={() => setImageError(true)}
        />
      </div>
      <style dangerouslySetInnerHTML={{ __html: `
        .filter-red {
          filter: brightness(0) saturate(100%)
                  invert(20%) sepia(75%) saturate(3500%) hue-rotate(355deg) brightness(85%) contrast(95%);
        }
        .dark .filter-red {
          filter: brightness(0) saturate(100%)
                  invert(35%) sepia(60%) saturate(1500%) hue-rotate(345deg) brightness(85%) contrast(90%);
        }

        .filter-blue {
          filter: brightness(0) saturate(100%)
                  invert(20%) sepia(80%) saturate(1800%) hue-rotate(205deg) brightness(88%) contrast(95%);
        }
        .dark .filter-blue {
          filter: brightness(0) saturate(100%)
                  invert(40%) sepia(55%) saturate(1200%) hue-rotate(195deg) brightness(88%) contrast(90%);
        }

        .filter-green {
          filter: brightness(0) saturate(100%)
                  invert(35%) sepia(60%) saturate(1500%) hue-rotate(85deg) brightness(90%) contrast(95%);
        }
        .dark .filter-green {
          filter: brightness(0) saturate(100%)
                  invert(50%) sepia(40%) saturate(1000%) hue-rotate(80deg) brightness(85%) contrast(88%);
        }
      ` }} />
    </div>
  );
}
